import { DatePickerInput } from "components/shared/datePicker/DatePickerInput";
import moment from "moment";
import React from "react";
import ReactDatePicker, { ReactDatePickerProps } from "react-datepicker";
import { FieldPath, FieldValues, useController, UseControllerProps } from "react-hook-form";
import { useIntl } from "react-intl";
import { TranslatedUseControllerOptions, useTranslatedValidations } from "shared/validations/useTranslatedValidations";
import styles from "./DatePicker.module.scss";
import { DatePickerHeader, IDatePickerHeaderProps } from "./DatePickerHeader";
import { datePickerMessages } from "./datePickerMessages";

type ReactDatePickerPropsToIgnore = "onChange" | "customTimeInput";

type Props<TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues>> =
    Omit<ReactDatePickerProps, ReactDatePickerPropsToIgnore>
    & Omit<UseControllerProps<TFieldValues, TFieldName>, "rules" | "defaultValue">
    & {
        rules?: TranslatedUseControllerOptions<TFieldValues, TFieldName>;
    };

export const DatePicker = <TFieldValues extends FieldValues, TFieldName extends FieldPath<TFieldValues>>({
    autoComplete,
    name,
    rules,
    shouldUnregister,
    control,
    disabled,
    ...rest
}: Props<TFieldValues, TFieldName>) => {
    const intl = useIntl();

    const translatedRules = useTranslatedValidations<TFieldValues, TFieldName>(rules);
    const { field, fieldState: { isTouched, invalid, error } } = useController({
        name,
        rules: translatedRules,
        shouldUnregister,
        control,
        disabled,
    });

    const customInput = <DatePickerInput
        invalid={isTouched && invalid}
        valid={isTouched && !invalid}
        data-cy={field.name}
        name={field.name}
        error={error?.message}
        ref={field.ref} />;

    return <div className={styles.wide}>
        <ReactDatePicker
            data-cy={`${field.name}Picker`}
            name={field.name}
            dayClassName={dayClassName}
            customInput={customInput}
            className="form-control"
            disabled={disabled}
            selected={field.value ? moment(field.value).toDate() : null}
            onSelect={onChange}
            onBlur={field.onBlur}
            onChange={onChange}
            onChangeRaw={onChangeRaw}
            placeholderText={moment.localeData().longDateFormat("L").toLocaleUpperCase()}
            dateFormat={moment.localeData().longDateFormat("L").replace("DD", "dd").replace("D", "d").replace("YYYY", "yyyy").replace("YY", "yy")}
            nextMonthButtonLabel={intl.formatMessage(datePickerMessages.nextMonthButtonLabel)}
            previousMonthButtonLabel={intl.formatMessage(datePickerMessages.previousMonthButtonLabel)}
            renderCustomHeader={renderHeader}
            disabledKeyboardNavigation={true}
            strictParsing={true}
            autoComplete={autoComplete ?? "off"}
            {...rest} />
    </div>;

    function renderHeader(headerProps: IDatePickerHeaderProps) {
        return <DatePickerHeader {...headerProps} />;
    }

    function dayClassName(date: Date) {
        return `day-${moment(date).format("YYYY-MM-DD")}`;
    }

    function onChange(date: Date | null) {
        const value = date && moment(date).format("YYYY-MM-DD");
        field.onChange(value || "");
        field.onBlur();
    }

    function onChangeRaw(event: React.FocusEvent<HTMLInputElement>) {
        const supportedFormats = [
            moment.localeData().longDateFormat("l"),
            moment.localeData().longDateFormat("L"),
            moment.localeData().longDateFormat("l").replace("YYYY", "YY"),
            moment.localeData().longDateFormat("L").replace("YYYY", "YY"),
            moment.localeData().longDateFormat("ll"),
            moment.localeData().longDateFormat("LL"),
        ];

        const date = moment(event.target.value, supportedFormats, true);

        onChange(date.isValid() ? date.toDate() : null);
    }
}
